import React, { useEffect, useState } from 'react';
import { Flex, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import AIEmailTimer from '@/components/AIEmailTimer';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { AI_EMAIL_TIMEOUT_MINUTES } from '@/common/constants';
import styles from './ResolutionWindowProgress.module.css';

dayjs.extend(utc);

interface ResolutionWindowProgressProps {
  /**
   * UTC timestamp string when AI email was initiated
   */
  aiStartTime: string | null | undefined;
  /**
   * Total timeout duration in minutes (default: 60)
   */
  timeoutMinutes?: number;
  /**
   * Callback when cancel button (X) is clicked
   */
  onCancel?: () => void;
  /**
   * Whether to show the cancel button
   */
  showCancelButton?: boolean;
  /**
   * Callback when timer expires
   */
  onExpire?: () => void;
}

/**
 * ResolutionWindowProgress Component
 *
 * Displays a candlestick-style progress bar showing elapsed and remaining time
 * for the AI email resolution window. Includes countdown timer and optional cancel button.
 *
 * @param aiStartTime - UTC timestamp when AI email was initiated
 * @param timeoutMinutes - Total timeout duration (default: 60 minutes)
 * @param onCancel - Callback when X button is clicked
 * @param showCancelButton - Whether to show the cancel button
 * @param onExpire - Callback when timer expires
 */
const ResolutionWindowProgress: React.FC<ResolutionWindowProgressProps> = ({
  aiStartTime,
  timeoutMinutes = AI_EMAIL_TIMEOUT_MINUTES,
  onCancel,
  showCancelButton = false,
  onExpire,
}) => {
  // Calculate progress percentage - update in real-time to match timer
  const [progressPercentage, setProgressPercentage] = useState<number>(0);

  useEffect(() => {
    if (!aiStartTime) {
      setProgressPercentage(0);
      return;
    }

    const calculateProgress = () => {
      try {
        const startTime = dayjs.utc(aiStartTime);
        const now = dayjs.utc();
        const elapsedSeconds = now.diff(startTime, 'second');
        const totalTimeoutSeconds = timeoutMinutes * 60;
        const percentage = Math.min(
          100,
          Math.max(0, (elapsedSeconds / totalTimeoutSeconds) * 100)
        );
        return percentage;
      } catch (error) {
        console.error('Error calculating progress:', error);
        return 0;
      }
    };

    // Calculate immediately to show correct progress when component mounts
    setProgressPercentage(calculateProgress());

    // Update every second to stay in sync with timer
    const interval = setInterval(() => {
      setProgressPercentage(calculateProgress());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [aiStartTime, timeoutMinutes]);

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className={styles.container}>
      <Flex align='center' gap={12} style={{ width: '100%' }}>
        {/* Timer text on the left */}
        <span className={styles.label}>
          <Tooltip
            title='Time lapse before AI closes the conversation'
            placement='top'
            overlayStyle={{
              fontSize: '11px',
              padding: '4px 8px',
              maxWidth: 'none',
            }}
            overlayInnerStyle={{
              whiteSpace: 'nowrap',
              padding: '4px 8px',
            }}
          >
            <span className={styles.labelText}>Resolution window</span>
          </Tooltip>{' '}
          <span className={styles.timer}>
            <AIEmailTimer
              aiStartTime={aiStartTime}
              timeoutMinutes={timeoutMinutes}
              format='short'
              onExpire={onExpire}
            />
          </span>
        </span>

        {/* Candlestick progress bar in the middle */}
        <div className={styles.track}>
          {/* Elapsed time (blue) */}
          <div
            className={styles.elapsed}
            // eslint-disable-next-line react/forbid-dom-props -- runtime progress width
            style={{ width: `${progressPercentage}%` }}
          />
          {/* Remaining time (gray) */}
          <div
            className={styles.remaining}
            // eslint-disable-next-line react/forbid-dom-props -- runtime progress width
            style={{ width: `${100 - progressPercentage}%` }}
          />
        </div>

        {/* Cancel button on the right */}
        {showCancelButton && onCancel && (
          <div onClick={handleCancel} className={styles.cancel}>
            <CloseOutlined
              style={{
                fontSize: '9px',
                color: 'var(--white)',
                fontWeight: 900,
                strokeWidth: 3,
                textShadow:
                  '0 0 1px var(--white), 0 0 1px var(--white), 0 0 1px var(--white)',
                filter: 'drop-shadow(0 0 0.5px var(--white))',
                lineHeight: 1,
                margin: 0,
                padding: 0,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          </div>
        )}
      </Flex>
    </div>
  );
};

export default ResolutionWindowProgress;
